import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import type { PublicSellerBrandingView, PurchasablePlanView } from "@keydials/api-client";
import { readableTextColour } from "@keydials/dial-renderer";
import { isPlausibleEmail } from "@keydials/ui";
import { api } from "../auth/AuthContext";
import { DiscountCodeField } from "../components/DiscountCodeField";
import { KdLogo } from "../components/KdMark";
import { readCode } from "../discountCode";
import { currentRef } from "../referralCode";
import { TermsAcceptance, type TermsState } from "../components/TermsAcceptance";
import { SITE_URL } from "../siteUrl";

/**
 * The public buy page (KEY-368) — a sessionless front door, like the guest landing and the enquiry form.
 *
 * Everything commercial comes from the server: which tiers exist, what they cost, whether the figure
 * includes VAT. Nothing here decides a price, so nothing here can get one wrong.
 */

/** Prices are authored monthly or annually and never converted, so the period is shown, not normalised. */
export function priceLabel(plan: PurchasablePlanView): string {
  const amount = (plan.amountPence / 100).toLocaleString("en-GB", {
    style: "currency",
    currency: plan.currency || "GBP",
    minimumFractionDigits: plan.amountPence % 100 === 0 ? 0 : 2,
  });
  return `${amount}/${plan.period === "Annual" ? "year" : "month"}`;
}

/**
 * The seller's colour, where they have one, on everything that is ours to colour: the chosen card, the
 * toggle, the buy button — with the ink on it measured for contrast rather than assumed white. Shared
 * with the trial-ended screen (KEY-634), which is the same shop shown to somebody coming back.
 */
export function sellerBrandStyle(seller: PublicSellerBrandingView | null | undefined): CSSProperties | undefined {
  const accent = seller?.accentColour ?? null;
  return accent
    ? ({
        "--accent": accent,
        "--accent-text": accent,
        "--buy-on-accent": readableTextColour(accent, "#ffffff", "#0f172a"),
        "--buy-brand": accent,
      } as CSSProperties)
    : undefined;
}

/**
 * Whose shop this is, above the price list. Undefined while the seller is still being read (a blank of
 * the same height, so nothing jumps); null is Key Dials' own price, which wears our branding.
 */
export function SellerBrand({ seller }: { seller: PublicSellerBrandingView | null | undefined }) {
  if (seller === undefined) return <div className="buy-brand" aria-hidden />;
  if (!seller) {
    return (
      <div className="buy-brand">
        <KdLogo height={30} />
      </div>
    );
  }
  return (
    <div className="buy-brand">
      {seller.logoUrl ? (
        <img className="buy-brand-logo" src={seller.logoUrl} alt={seller.displayName} />
      ) : (
        <span className="buy-brand-name">{seller.displayName}</span>
      )}
      <span className="buy-brand-powered">
        Powered by <KdLogo height={16} />
      </span>
      {seller.pageSlug && (
        <a className="buy-brand-back" href={`${SITE_URL}/partners/${seller.pageSlug}/`}>
          ← Back to {seller.displayName}
        </a>
      )}
    </div>
  );
}

/**
 * A partner's shop is shown light, whatever the visitor's theme. Their logo and colour were chosen
 * against a light ground (the enquiry form pins light for the same reason), and a dark-green logo on
 * near-black is unreadable. Restored when the page goes.
 */
export function usePartnerLightTheme(seller: PublicSellerBrandingView | null | undefined): void {
  useEffect(() => {
    if (!seller) return;
    const root = document.documentElement;
    const previous = root.getAttribute("data-theme");
    root.setAttribute("data-theme", "professional");
    return () => {
      if (previous) root.setAttribute("data-theme", previous);
    };
  }, [seller]);
}

/**
 * What a tier hands over, in one line. A bundle names its dials ("1 Personal Estate Plan dial · 2 people
 * on each"); a build-your-own tier describes its allowances. Never both — a bundle's "0 dials to build"
 * allowance reads as "you get nothing". CampaignJoin shares this, so a QR-campaign card reads the same.
 */
export function includesLabel(plan: PurchasablePlanView): string {
  const bundled = plan.includedDefinitions ?? [];
  if (plan.familySeats) {
    // A family pack (KEY-651), worded around the people it covers (KEY-711) exactly as the partner page's
    // card words it: the buyer plus each relative they invite has their own dial, shared with the rung's
    // other seats. The seller's own sentence still wins when they wrote one (KEY-691).
    const noun = bundled.length > 0 ? bundled.map((d) => `${d.name} dial`).join(" and ") : "dial";
    const places = `${plan.familySeats + 1} family members have their own ${noun}`;
    const share = plan.usersPerUnit !== null && plan.usersPerUnit > 1 ? plan.usersPerUnit - 1 : null;
    if (plan.cardLine) return `${places} · ${plan.cardLine}`;
    return share
      ? `${places} · Each person can share their personal ${noun} with up to ${share} trusted ${share === 1 ? "person" : "people"}, and can control exactly what they see`
      : places;
  }
  if (bundled.length > 0) {
    const dials = bundled.map((d) => `${d.instances} ${d.name} ${d.instances === 1 ? "dial" : "dials"}`).join(" · ");
    // The seller's own sentence, when they wrote one (KEY-691), takes the people count's place.
    return plan.cardLine
      ? `${dials} · ${plan.cardLine}`
      : plan.usersPerUnit
        ? `${dials} · ${plan.usersPerUnit} ${plan.usersPerUnit === 1 ? "person" : "people"} on each`
        : dials;
  }
  const build = `${dialsLabel(plan.maxDefinitions)} · ${copiesLabel(plan.instancesPerDefinition)}`;
  return plan.cardLine ? `${build} · ${plan.cardLine}` : build;
}

/** How many kinds of dial the tier lets you build. */
export function dialsLabel(value: number | null): string {
  if (value === null) return "Unlimited dials";
  return `${value} ${value === 1 ? "dial" : "dials"}`;
}

/**
 * How many of each dial you can run. Deliberately "copies" rather than "instances": a person reading a
 * price list is not holding our domain vocabulary, and to them each one simply IS a dial.
 */
export function copiesLabel(value: number | null): string {
  if (value === null) return "unlimited copies each";
  return `up to ${value} ${value === 1 ? "copy" : "copies"} each`;
}

export function BuyPlans() {
  const [plans, setPlans] = useState<PurchasablePlanView[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /**
   * The plan the visitor already picked, if they arrived from the pricing page's "Get started".
   *
   * Read once from the URL and validated as a GUID before it is used, because it arrives in a link
   * anybody can edit. An id that matches no purchasable plan is ignored rather than surfaced: the
   * page still works, it just falls back to the first tier, which is a better outcome than an error
   * about a plan the visitor never chose.
   */
  const requestedPlanId = useMemo(() => {
    const raw = new URLSearchParams(window.location.search).get("plan");
    return raw && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(raw)
      ? raw
      : null;
  }, []);

  /**
   * The discount code a buy link carries (KEY-642), read once like the plan. It narrows the shelf to the
   * plans the code works on — a friends-and-family link for Personal should not offer Commercial at a
   * price the code will then refuse. The server decides which those are, and ignores a code that works
   * on nothing on sale, so a stale link still shows the whole shop.
   */
  const linkCode = useMemo(() => readCode(window.location.search) || null, []);

  /**
   * The affiliate who sent this buyer (KEY-514): the link's `?ref`, or one remembered from a link in the
   * last 30 days. Sent with the checkout and nothing more — the server ignores a dead one, so it can never
   * stop a purchase.
   */
  const referral = useMemo(() => currentRef(window.location.search), []);

  /**
   * The discount that referral gives (KEY-514), pre-filled in the code box so the buyer sees what comes off
   * before Stripe does. Undefined while asking; null for none. A link's own `?code=` takes precedence.
   */
  const [referralDiscount, setReferralDiscount] = useState<string | null | undefined>(
    referral && !linkCode ? undefined : null,
  );
  useEffect(() => {
    if (!referral || linkCode) return;
    let cancelled = false;
    api
      .getReferralDiscountCode(referral)
      .then((code) => !cancelled && setReferralDiscount(code))
      .catch(() => !cancelled && setReferralDiscount(null));
    return () => {
      cancelled = true;
    };
  }, [referral, linkCode]);

  const [chosen, setChosen] = useState<string | null>(null);
  /**
   * Monthly or annual. Seeded from the plan the link asked for, so somebody who clicked a yearly price
   * on the website arrives on the yearly view rather than on a monthly one with their choice missing.
   */
  const [period, setPeriod] = useState<"Monthly" | "Annual">("Monthly");
  const [email, setEmail] = useState("");
  /** The End User Terms version accepted with both boxes ticked (KEY-618); null blocks the checkout. */
  const [acceptedVersion, setAcceptedVersion] = useState<string | null>(null);
  /** Bumped when the server says the terms changed under this page, so they reload and are re-accepted. */
  const [termsReload, setTermsReload] = useState(0);
  const onTerms = useCallback((state: TermsState) => setAcceptedVersion(state.acceptedVersion), []);
  /**
   * Whose shop this is. A buyer who pressed Buy Now on a partner's page should still see that partner —
   * their logo and colour — when choosing a plan, not be dropped into a Key Dials page they never asked
   * for. Undefined while loading; null is Key Dials' own price, which wears our branding.
   */
  const [seller, setSeller] = useState<PublicSellerBrandingView | null | undefined>(requestedPlanId ? undefined : null);
  /** The discount code in the box (KEY-642), applied or not — the server has the final say. */
  const [discountCode, setDiscountCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      // KEY-421: scoped to the seller of the price they arrived for. Somebody who clicked "Get started"
      // on a partner's page is in that partner's shop; without this they were shown Key Dials' catalogue
      // and every other specialist's next to it — a market they never walked into.
      .listPurchasablePlans(requestedPlanId, linkCode)
      .then((list) => {
        if (cancelled) return;
        setPlans(list);
        const requested = list.find((p) => p.id === requestedPlanId);
        if (requested?.period === "Annual") setPeriod("Annual");
        setChosen(requested?.id ?? list[0]?.id ?? null);
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : "Couldn't load the plans.");
      });
    return () => {
      cancelled = true;
    };
  }, [requestedPlanId, linkCode]);

  useEffect(() => {
    if (!requestedPlanId) return;
    let cancelled = false;
    api
      .getPlanSeller(requestedPlanId)
      .then((s) => !cancelled && setSeller(s))
      // Branding is a nicety: if it can't be read, the page simply wears ours.
      .catch(() => !cancelled && setSeller(null));
    return () => {
      cancelled = true;
    };
  }, [requestedPlanId]);

  usePartnerLightTheme(seller);

  /**
   * Whether the toggle means anything: some family has to be sold BOTH monthly and yearly. A shop whose
   * rungs are each sold one way only (a partner's annual-only list, say) showed a Monthly/Yearly switch
   * that changed nothing — every family keeps its tiers in both views.
   */
  const offersBothPeriods = useMemo(() => {
    const periods = new Map<string, Set<string>>();
    for (const p of plans ?? []) {
      const set = periods.get(p.familyLabel) ?? new Set<string>();
      set.add(p.period);
      periods.set(p.familyLabel, set);
    }
    return [...periods.values()].some((set) => set.has("Monthly") && set.has("Annual"));
  }, [plans]);

  const families = useMemo(() => {
    const groups = new Map<string, PurchasablePlanView[]>();
    for (const plan of plans ?? []) {
      const existing = groups.get(plan.familyLabel);
      if (existing) existing.push(plan);
      else groups.set(plan.familyLabel, [plan]);
    }
    // A family sold in only one period keeps its tiers in both views. Commercial is monthly only, and
    // switching to yearly should not make it disappear — it should show what it actually costs.
    return [...groups.entries()].map(([label, tiers]) => {
      const inPeriod = tiers.filter((t) => t.period === period);
      return [label, inPeriod.length > 0 ? inPeriod : tiers] as const;
    });
  }, [plans, period]);

  /* Keep the selection on something the buyer can see. Switching period would otherwise leave the
     chosen id pointing at a hidden card, and the Buy button would charge for a plan not on screen. */
  useEffect(() => {
    const visible = families.flatMap(([, tiers]) => tiers);
    if (visible.length > 0 && !visible.some((p) => p.id === chosen)) {
      setChosen(visible[0].id);
    }
  }, [families, chosen]);

  const selected = plans?.find((p) => p.id === chosen) ?? null;

  const buy = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.startPublicCheckout({
        planId: selected.id,
        email: email.trim(),
        termsVersion: acceptedVersion,
        ageConfirmed: acceptedVersion !== null,
        discountCode,
        affiliateCode: referral,
      });
      // Stripe's hosted page takes it from here — card details never touch this app.
      window.location.assign(url);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Couldn't start your purchase.";
      // The terms moved on while this page was open: show the new text and ask again.
      if (/updated/i.test(message)) setTermsReload((n) => n + 1);
      setError(message);
      setBusy(false);
    }
  };

  const brandStyle = sellerBrandStyle(seller);
  const brand = <SellerBrand seller={seller} />;

  if (loadError) {
    return (
      <main className="buy-page" style={brandStyle}>
        {brand}
        <h1>Plans</h1>
        <p className="buy-error">{loadError}</p>
      </main>
    );
  }

  if (!plans) {
    return (
      <main className="buy-page" style={brandStyle}>
        {brand}
        <p className="buy-muted">Loading plans…</p>
      </main>
    );
  }

  return (
    <main className="buy-page" style={brandStyle}>
      {brand}
      <header className="buy-header">
        <h1>Choose a plan</h1>
        {/* True of every tier on the shelf, card-free ones included (KEY-638): a card-free trial takes no
            card and is managed from the same place once a card is added. */}
        <p className="buy-muted">
          Pay by card through Stripe — or, where a plan offers it, start a free trial with no card at all. Once
          you're in, your plan lives under Your settings, where you can add a card or cancel.
        </p>
        {offersBothPeriods && (
          <div className="buy-period" role="group" aria-label="Billing period">
            {(["Monthly", "Annual"] as const).map((option) => (
              <button
                key={option}
                type="button"
                className={`buy-period-choice${period === option ? " is-chosen" : ""}`}
                aria-pressed={period === option}
                onClick={() => setPeriod(option)}
              >
                {option === "Monthly" ? "Monthly" : "Yearly"}
              </button>
            ))}
          </div>
        )}
      </header>

      {families.map(([familyLabel, tiers]) => (
        <section key={familyLabel} className="buy-family">
          <h2>{familyLabel}</h2>
          <div className="buy-tiers">
            {tiers.map((plan) => {
              const isChosen = plan.id === chosen;
              return (
                <button
                  key={plan.id}
                  type="button"
                  className={`buy-tier${isChosen ? " is-chosen" : ""}`}
                  aria-pressed={isChosen}
                  onClick={() => setChosen(plan.id)}
                >
                  <span className="buy-tier-name">{plan.displayName ?? plan.tierName}</span>
                  <span className="buy-tier-price">{priceLabel(plan)}</span>
                  {/* Which figure a buyer is looking at matters: the cheaper number is not the cheaper
                      plan if one includes VAT and the other doesn't. */}
                  <span className="buy-tier-vat">{plan.taxInclusive ? "including VAT" : "plus VAT"}</span>
                  <span className="buy-tier-includes">{includesLabel(plan)}</span>
                  {plan.trialDays > 0 && (
                    <span className="buy-tier-trial">
                      {/* "Free" either way, as the website says it (KEY-638): a card-up-front trial charges
                          nothing until it ends, and the line under the button says what the card is for. */}
                      {plan.cardFreeTrial
                        ? `${plan.trialDays}-day free trial — no card needed`
                        : `${plan.trialDays}-day free trial`}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      ))}

      <TermsAcceptance
        period={selected ? (selected.period === "Annual" ? "Annual" : "Monthly") : null}
        cardFreeTrial={!!selected?.cardFreeTrial && selected.trialDays > 0}
        disabled={busy}
        reloadKey={termsReload}
        onChange={onTerms}
      />

      <section className="buy-checkout">
        <label htmlFor="buy-email">Your email address</label>
        <input
          id="buy-email"
          type="email"
          autoComplete="email"
          value={email}
          disabled={busy}
          placeholder="you@yourcompany.co.uk"
          onChange={(e) => setEmail(e.target.value)}
        />
        <p className="buy-muted">
          This is how you'll sign in. We'll email you a code each time — there's no password to remember.
        </p>

        <DiscountCodeField
          // Remounted once the referral's discount is known, so it is read as the starting code.
          key={referralDiscount ?? "none"}
          initialCode={referralDiscount}
          planId={selected?.id ?? null}
          period={selected?.period ?? "Monthly"}
          fullPricePence={selected?.amountPence}
          disabled={busy}
          onChange={setDiscountCode}
        />

        {/* Everything after this button says Key Dials, not the partner — say so before they pay. */}
        {seller && (
          <p className="buy-muted">
            {seller.displayName} is licensed by Key Dials Limited, so your account, sign-in emails and receipts come
            from Key Dials.
          </p>
        )}

        {error && <p className="buy-error">{error}</p>}

        <button className="buy-go" type="button" disabled={busy || !selected || !isPlausibleEmail(email) || !acceptedVersion} onClick={buy}>
          {busy
            ? "Taking you to checkout…"
            : selected?.trialDays
              ? `Start your ${selected.trialDays}-day free trial`
              : "Continue to payment"}
        </button>

        {/* Which promise the trial makes depends on whether a card is taken — saying "your card is taken
            now" about a card-free tier would be flatly untrue, and the reassurance differs too. */}
        {selected && selected.trialDays > 0 && (
          <p className="buy-muted">
            {selected.cardFreeTrial
              ? "No card needed and nothing is charged. When the trial ends it simply stops — your dials are kept, and subscribing brings them straight back."
              : "Your card is taken now and charged when the trial ends. Cancel before then and you pay nothing."}
          </p>
        )}
      </section>
    </main>
  );
}
